// ============================================================================
// JARVISTRAVEL - ADD TO HOME SCREEN (PWA install prompt)
// The platform truth, the per-device counter, and the beforeinstallprompt
// capture that the post-login nudge is built on (JAR-2031).
//
// There is NO API to place an app on the home screen for the user. The most a
// browser allows is:
//   - Android / Chromium: capture `beforeinstallprompt`, and on the user's tap
//     call `.prompt()` to open the real native install dialog.
//   - iOS (all browsers are WebKit): nothing — we can only point at the Share
//     button. `isIOS()` drives that instruction-only variant.
// ============================================================================

/**
 * Minimal shape of the non-standard `beforeinstallprompt` event. Not in the DOM
 * lib types, so declared here rather than pulled from a dependency.
 */
interface BeforeInstallPromptEvent extends Event {
  readonly platforms: string[];
  prompt(): Promise<void>;
  readonly userChoice: Promise<{ outcome: 'accepted' | 'dismissed'; platform: string }>;
}

// --- module state -----------------------------------------------------------

let deferredPrompt: BeforeInstallPromptEvent | null = null;
// Fallback session guard for devices where sessionStorage throws (private mode,
// blocked storage). Without it, every activation counts and the counter inflates
// past the milestones on the first eligible session (web-app#536 review, L2).
// In-memory only, so it resets on reload — acceptable for a blocked-storage edge.
let memorySessionCounted = false;
const listeners = new Set<() => void>();
const notify = () => listeners.forEach((fn) => fn());
// Takes back the window listeners initInstallPrompt added; null until it runs.
let stopListening: (() => void) | null = null;

/** Subscribe to "the install picture changed" (a deferred prompt arrived, or the
 *  app was installed). Returns an unsubscribe. Android fires
 *  `beforeinstallprompt` asynchronously, often after the app has mounted, so the
 *  UI must be able to re-check rather than read a one-shot value at mount. */
export function subscribe(fn: () => void): () => void {
  listeners.add(fn);
  return () => {
    listeners.delete(fn);
  };
}

// --- storage keys -----------------------------------------------------------

/**
 * Persistent, DEVICE-scoped state. Uses the `jt.` storage-ownership prefix (the
 * repo convention) and is listed in `localDataPurge.ts`'s `PRESERVE` set, so it
 * SURVIVES `purgeAllLocalUserData()` on sign-out.
 *
 * A logout user must be nudged on their 1st, 3rd and 6th *sign-in*. If the
 * counter reset on every sign-out (which purges every owned key), that user
 * would sit at count 1 on every single login and be prompted every time. It
 * holds no personal data — a small integer and one boolean. Preserving it is the
 * sanctioned mechanism (PRESERVE), not an off-namespace key that dodges the
 * sweep (web-app#536 review, M2).
 */
const STATE_KEY = 'jt.a2hs.install.v1';

/**
 * Per-session guard. Prefixed `jt.` ON PURPOSE: the existing sign-out and
 * session-expiry purge (`purgeAllLocalUserData`) already clears every `jt.` key
 * from sessionStorage, so the next sign-in becomes a fresh activation for free —
 * that is exactly what makes the count advance per *sign-in* for a logout user,
 * and once per *session* for one who never logs out.
 */
const SESSION_KEY = 'jt.a2hs.session';

/** The activations we nudge on. */
const MILESTONES = [1, 3, 6];

interface InstallState {
  count: number;
  /** Installed, or accepted the native prompt — never nudge again. */
  done: boolean;
}

function readState(): InstallState {
  try {
    const raw = localStorage.getItem(STATE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw) as Partial<InstallState>;
      return { count: parsed.count ?? 0, done: !!parsed.done };
    }
  } catch {
    /* storage disabled/blocked — treat as fresh */
  }
  return { count: 0, done: false };
}

function writeState(state: InstallState): void {
  try {
    localStorage.setItem(STATE_KEY, JSON.stringify(state));
  } catch {
    /* best effort — a nudge that can't persist is better than a thrown sign-in */
  }
}

/** Stop nudging on this device — the app is installed or the user accepted. */
export function markDone(): void {
  writeState({ ...readState(), done: true });
}

// --- platform detection -----------------------------------------------------

/** Running as an installed app (standalone display), so already on a home screen. */
export function isStandalone(): boolean {
  try {
    return (
      window.matchMedia('(display-mode: standalone)').matches ||
      // iOS Safari does not report standalone via matchMedia.
      (window.navigator as unknown as { standalone?: boolean }).standalone === true
    );
  } catch {
    return false;
  }
}

export function isIOS(): boolean {
  const ua = navigator.userAgent || '';
  const iDevice = /iPad|iPhone|iPod/.test(ua);
  // iPadOS 13+ masquerades as a Mac; a touch-capable "Mac" is really an iPad.
  const iPadOS = navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1;
  return iDevice || iPadOS;
}

/** Phones and tablets only — the feature is for a device with a home screen, not
 *  a desktop where Chrome would still fire `beforeinstallprompt`. */
function isProbablyMobile(): boolean {
  try {
    return window.matchMedia('(pointer: coarse)').matches || isIOS();
  } catch {
    return isIOS();
  }
}

export function hasNativePrompt(): boolean {
  return deferredPrompt !== null;
}

// --- the decision -----------------------------------------------------------

export type PromptVariant = 'native' | 'ios';

export interface ActivationDecision {
  show: boolean;
  variant?: PromptVariant;
  milestone?: number;
}

/**
 * Evaluate this activation. Counts it AT MOST once per browser session (the
 * sessionStorage guard) and ONLY when a prompt could actually be shown, so a
 * milestone is never burned on a session where nothing could appear. Because a
 * single activation can bump the counter only once, it can never produce both a
 * "session" nudge and a "sign-in" nudge — the "never both" rule holds by
 * construction, not by a second check.
 */
export function evaluateActivation(): ActivationDecision {
  if (isStandalone()) return { show: false }; // already installed
  if (!isProbablyMobile()) return { show: false }; // phones only

  const state = readState();
  if (state.done) return { show: false }; // installed / accepted earlier

  const variant: PromptVariant | null = hasNativePrompt()
    ? 'native'
    : isIOS()
      ? 'ios'
      : null;
  // Android before the event has fired: don't count, so the milestone survives
  // to the moment we can actually open the native dialog.
  if (!variant) return { show: false };

  let alreadyCounted = false;
  try {
    alreadyCounted = sessionStorage.getItem(SESSION_KEY) === '1';
  } catch {
    // sessionStorage unavailable — fall back to the in-memory guard so a blocked
    // device counts this activation at most once per page-load, not every render.
    alreadyCounted = memorySessionCounted;
  }
  // This session's activation was already counted, so it was already decided —
  // at most one prompt per session, even if the caller re-evaluates.
  if (alreadyCounted) return { show: false };

  const count = state.count + 1;
  writeState({ ...state, count });
  try {
    sessionStorage.setItem(SESSION_KEY, '1');
  } catch {
    // Storage blocked: remember in memory for the rest of this page-load.
    memorySessionCounted = true;
  }

  if (MILESTONES.includes(count)) return { show: true, variant, milestone: count };
  return { show: false };
}

/**
 * Open the native install dialog (Android/Chromium). One-shot: a deferred prompt
 * can only be used once, so it is cleared on use. Returns the user's choice, or
 * 'unavailable' when there is nothing to show (iOS, or the event never fired).
 */
export async function promptInstall(): Promise<'accepted' | 'dismissed' | 'unavailable'> {
  const dp = deferredPrompt;
  if (!dp) return 'unavailable';
  deferredPrompt = null;
  try {
    await dp.prompt();
    const { outcome } = await dp.userChoice;
    if (outcome === 'accepted') markDone();
    return outcome;
  } catch {
    return 'dismissed';
  }
}

/**
 * Register the window listeners. Called once from `main.tsx` boot, BEFORE React
 * renders, so a `beforeinstallprompt` that fires early (e.g. while the user is
 * still on the sign-in screen) is captured and waiting by the time the
 * authenticated shell asks for it.
 */
export function initInstallPrompt(): void {
  // Once: a second call would add a second pair of listeners, and every
  // prompt would then be captured and announced twice.
  if (typeof window === 'undefined' || stopListening) return;
  const onPrompt = (e: Event) => {
    // Stop Chrome's own mini-infobar; we drive the prompt from our own UI.
    e.preventDefault();
    deferredPrompt = e as BeforeInstallPromptEvent;
    notify();
  };
  const onInstalled = () => {
    deferredPrompt = null;
    markDone();
    notify();
  };
  window.addEventListener('beforeinstallprompt', onPrompt);
  window.addEventListener('appinstalled', onInstalled);
  stopListening = () => {
    window.removeEventListener('beforeinstallprompt', onPrompt);
    window.removeEventListener('appinstalled', onInstalled);
  };
}

// --- test seams (not for production callers) ---------------------------------

/** @internal test-only: inject/clear the deferred prompt. */
export function __setDeferredPromptForTest(value: unknown): void {
  deferredPrompt = (value as BeforeInstallPromptEvent) ?? null;
}

/**
 * @internal test-only: clear all module-level state between tests. Wired into
 * `src/test/setup.ts`'s afterEach so a captured prompt or a lingering subscriber
 * from one test cannot leak into the next (moduleStateIsReset.test.ts).
 */
export function __resetInstallPromptState(): void {
  // The window listeners too: left in place, a prompt dispatched after the
  // reset would be captured again by a test that never asked for it.
  stopListening?.();
  stopListening = null;
  deferredPrompt = null;
  memorySessionCounted = false;
  listeners.clear();
}
